import { AlertCircle, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";

import { useOrganizationsPortfolio } from "../hooks/useOrganizationsPortfolio";
import type { OrganizationTreeItem } from "../types/clients-api.types";
import { canEditOrganization, canReadClientAccounts } from "../utils/clientsPermissions";
import { ClientAccountsModal } from "./ClientAccountsModal";
import { ClientsFiltersBar } from "./ClientsFiltersBar";
import { ClientsKpiStrip } from "./ClientsKpiStrip";
import { EditOrganizationModal } from "./EditOrganizationModal";
import { OrganizationsTable } from "./OrganizationsTable";

export interface OrganizationsPortfolioViewProps {
  /** Increment to force a refetch after an external mutation (e.g. the create-organization modal). */
  refreshToken?: number;
  /** Opens the create-organization flow from the empty state CTA. */
  onCreateOrganization?: () => void;
}

/**
 * Level 1 of the internal master-detail: KPI strip, organization filters, and the organizations table.
 * Clicking a row (or its "view clients" action) drills down to `/app/clients/organizations/:id`.
 */
export function OrganizationsPortfolioView({
  refreshToken = 0,
  onCreateOrganization,
}: OrganizationsPortfolioViewProps) {
  const { t } = useTranslations("clients");
  const { user } = useAuth();
  const navigate = useNavigate();
  const query = useOrganizationsPortfolio();

  const [editOrganizationId, setEditOrganizationId] = useState<string | null>(null);
  // Rows are organizations, so this holds an organization id — the accounts drawer runs in "organization" scope.
  const [accountsModal, setAccountsModal] = useState<{ organizationId: string | null; displayName: string }>({
    organizationId: null,
    displayName: "",
  });

  const refetch = query.refetch;
  const lastRefreshToken = useRef(refreshToken);
  useEffect(() => {
    if (refreshToken !== lastRefreshToken.current) {
      lastRefreshToken.current = refreshToken;
      refetch();
    }
  }, [refreshToken, refetch]);

  const openOrganization = (organization: OrganizationTreeItem) => {
    navigate(`/app/clients/organizations/${organization.id}`);
  };

  return (
    <div className="space-y-4">
      <ClientsKpiStrip scope="organizations" total={query.total} isLoading={query.isLoading} />

      <ClientsFiltersBar
        mode="organizations"
        filters={query.filters}
        setFilters={query.setFilters}
        appliedFilters={query.appliedFilters}
        onApply={query.applyFilters}
        onReset={query.resetFilters}
        onRemoveFilter={query.removeFilter}
        isLoading={query.isLoading}
      />

      {query.error && (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={
            <span className="flex flex-wrap items-center gap-3">
              {query.error}
              <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={refetch}>
                {t("error_retry")}
              </ColocaButton>
            </span>
          }
        />
      )}

      <OrganizationsTable
        rows={query.rows}
        isLoading={query.isLoading}
        hasActiveFilters={query.hasActiveFilters}
        page={query.page}
        pageSize={query.pageSize}
        total={query.total}
        onPageChange={query.setPage}
        onPageSizeChange={query.setPageSize}
        onOpenOrganization={openOrganization}
        onEditOrganization={canEditOrganization(user) ? setEditOrganizationId : undefined}
        onViewAccounts={
          canReadClientAccounts(user)
            ? (organization) =>
                setAccountsModal({ organizationId: organization.id, displayName: organization.name })
            : undefined
        }
        onClearFilters={query.resetFilters}
        onCreateOrganization={onCreateOrganization}
      />

      <EditOrganizationModal
        isOpen={editOrganizationId !== null}
        onClose={() => setEditOrganizationId(null)}
        organizationId={editOrganizationId}
        onSuccess={refetch}
      />

      <ClientAccountsModal
        isOpen={accountsModal.organizationId !== null}
        onClose={() => setAccountsModal({ organizationId: null, displayName: "" })}
        entityId={accountsModal.organizationId}
        entityDisplayName={accountsModal.displayName}
        scope="organization"
      />
    </div>
  );
}
